import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  Menu,
  Shield,
  User,
  X,
  Zap,
} from "lucide-react";
import { type CSSProperties, useState } from "react";
import { authClient } from "@/lib/auth-client";
import { m } from "@/paraglide/messages";
import { tools } from "@/tools";
import { SearchBox } from "./SearchBox";
import ThemeToggle from "./ThemeToggle";

function ToolIcon({ icon, size = 20 }: { icon: string; size?: number }) {
  if (icon.startsWith("/")) {
    return (
      <img
        src={icon}
        alt=""
        width={size}
        height={size}
        className="object-contain"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="leading-none"
      style={{ fontSize: size * 0.9 }}
    >
      {icon}
    </span>
  );
}

function UserMenu() {
  const { data: session, isPending } = authClient.useSession();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  if (isPending) return null;

  if (!session?.user) {
    return (
      <Link
        to="/login"
        className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        {m.nav_sign_in()}
      </Link>
    );
  }

  const user = session.user;
  const initials = user.name
    ? user.name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase();

  async function handleSignOut() {
    await authClient.signOut();
    navigate({ to: "/" });
    setIsOpen(false);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
        aria-label={m.nav_user_menu()}
      >
        {user.image ? (
          <img
            src={user.image}
            alt={user.name || user.email}
            className="w-9 h-9 rounded-full object-cover"
          />
        ) : (
          initials
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-56 rounded-lg border border-border bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden animate-slide-up">
          <div className="px-4 py-3 border-b border-border">
            {user.name && (
              <p className="text-sm font-medium text-foreground">{user.name}</p>
            )}
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
          </div>
          <div className="p-1">
            {(user as { isAdmin?: boolean }).isAdmin && (
              <Link
                to="/admin/contact"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
              >
                <Shield className="h-4 w-4" />
                {m.nav_admin()}
              </Link>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
            >
              <User className="h-4 w-4" />
              {m.nav_sign_out()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const panelClass =
  "absolute left-0 top-full mt-2 w-72 rounded-xl border shadow-2xl shadow-black/60 overflow-hidden animate-slide-up z-[70]";
const panelStyle: CSSProperties = {
  background: "var(--surface-3)",
  borderColor: "var(--border-c)",
};
const triggerClass =
  "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium text-foreground transition-colors";
const triggerStyle: CSSProperties = {
  background: "var(--surface-2)",
  borderColor: "var(--border-c)",
};
const itemClass = (active: boolean) =>
  `flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors ${
    active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted/60"
  }`;

export default function SiteHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false);
  const [isToolMenuOpen, setIsToolMenuOpen] = useState(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const currentTool = tools.find((tool) =>
    currentPath.startsWith(tool.basePath),
  );
  const pages = currentTool?.pages ?? [];
  const hasSub = pages.length > 1;
  const currentPage =
    pages.find((p) => p.path === currentPath) ??
    pages.find(
      (p) => p.path !== currentTool?.basePath && currentPath.startsWith(p.path),
    ) ??
    pages.find((p) => p.path === currentTool?.basePath);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70" />

      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between gap-3">
          {/* Logo */}
          <Link
            to="/"
            className="group flex shrink-0 items-center gap-1.5 text-foreground transition-colors hover:text-primary"
          >
            <img
              src="/toolverse-icon.svg"
              alt="Toolverse"
              width="40"
              height="40"
              fetchPriority="high"
              className="h-10 w-10 object-contain"
            />
            <span className="hidden font-display text-lg font-bold tracking-wider sm:inline">
              TOOLVERSE
            </span>
          </Link>

          {/* Center: tool + sub-tool selectors (desktop) */}
          <nav className="hidden flex-1 items-center justify-center gap-1.5 md:flex">
            {/* Tool selector — SPA links (tool base paths are static routes) */}
            <div className="relative">
              <button
                type="button"
                aria-expanded={isToolMenuOpen}
                onClick={() => {
                  setIsToolMenuOpen((v) => !v);
                  setIsSubMenuOpen(false);
                }}
                onBlur={() => setTimeout(() => setIsToolMenuOpen(false), 150)}
                className={triggerClass}
                style={triggerStyle}
              >
                {currentTool ? (
                  <ToolIcon icon={currentTool.icon} />
                ) : (
                  <Zap className="h-4 w-4 text-primary" />
                )}
                <span className="max-w-[10rem] truncate">
                  {currentTool ? currentTool.name() : m.nav_tools()}
                </span>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${isToolMenuOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isToolMenuOpen && (
                <div className={panelClass} style={panelStyle}>
                  <div className="px-3 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    {m.nav_available_tools()}
                  </div>
                  <div className="max-h-[70vh] overflow-y-auto p-1">
                    {tools.map((tool) => (
                      <Link
                        key={tool.id}
                        to={tool.basePath}
                        onClick={() => setIsToolMenuOpen(false)}
                        className={itemClass(
                          currentPath.startsWith(tool.basePath),
                        )}
                      >
                        <ToolIcon icon={tool.icon} size={22} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">
                            {tool.name()}
                          </div>
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {tool.description()}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sub-tool selector — plain anchors (sub-pages may be dynamic
                routes; TanStack <Link to={runtimeString}> leaves the param
                stale, so a real navigation is used instead). */}
            {currentTool && hasSub && (
              <>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                <div className="relative">
                  <button
                    type="button"
                    aria-expanded={isSubMenuOpen}
                    onClick={() => {
                      setIsSubMenuOpen((v) => !v);
                      setIsToolMenuOpen(false);
                    }}
                    onBlur={() =>
                      setTimeout(() => setIsSubMenuOpen(false), 150)
                    }
                    className={triggerClass}
                    style={triggerStyle}
                  >
                    {currentPage && (
                      <ToolIcon icon={currentPage.icon} size={18} />
                    )}
                    <span className="max-w-[10rem] truncate">
                      {currentPage ? currentPage.name() : currentTool.name()}
                    </span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${isSubMenuOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isSubMenuOpen && (
                    <div className={panelClass} style={panelStyle}>
                      <div className="px-3 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        {currentTool.name()}
                      </div>
                      <div className="max-h-[70vh] overflow-y-auto p-1">
                        {pages.map((page) => (
                          <a
                            key={page.path}
                            href={page.path}
                            className={itemClass(page.path === currentPath)}
                          >
                            <ToolIcon icon={page.icon} size={20} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium">
                                {page.name()}
                              </div>
                              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                {page.description()}
                              </p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </nav>

          {/* Search + theme + auth (desktop) */}
          <div className="flex shrink-0 items-center gap-1.5">
            <SearchBox />
            <ThemeToggle />
            <div className="hidden items-center md:flex">
              <UserMenu />
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:hidden"
              aria-label={
                isMobileMenuOpen ? m.nav_close_menu() : m.nav_open_menu()
              }
              aria-expanded={isMobileMenuOpen}
            >
              <div className="relative h-5 w-5">
                <Menu
                  className={`absolute inset-0 h-5 w-5 transition-all duration-300 ${isMobileMenuOpen ? "rotate-90 opacity-0" : "rotate-0 opacity-100"}`}
                />
                <X
                  className={`absolute inset-0 h-5 w-5 transition-all duration-300 ${isMobileMenuOpen ? "rotate-0 opacity-100" : "-rotate-90 opacity-0"}`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile navigation */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out md:hidden ${
            isMobileMenuOpen ? "max-h-[85vh] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <nav className="space-y-3 border-t border-border py-4">
            {/* Tool switcher — collapsible so a long tool list never blows up
                the menu. Tapping a tool navigates; the sub-pages below then
                reflect the new tool. */}
            <div>
              <button
                type="button"
                onClick={() => setIsMobileToolsOpen((v) => !v)}
                aria-expanded={isMobileToolsOpen}
                className="flex w-full items-center justify-between rounded-lg border px-4 py-3"
                style={triggerStyle}
              >
                <span className="flex items-center gap-3 font-medium">
                  {currentTool ? (
                    <ToolIcon icon={currentTool.icon} size={22} />
                  ) : (
                    <Zap className="h-5 w-5 text-primary" />
                  )}
                  {currentTool ? currentTool.name() : m.nav_tools()}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {m.nav_tools()}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${isMobileToolsOpen ? "rotate-180" : ""}`}
                  />
                </span>
              </button>
              {isMobileToolsOpen && (
                <div
                  className="mt-1 max-h-64 space-y-1 overflow-y-auto rounded-lg border p-1"
                  style={panelStyle}
                >
                  {tools.map((tool) => (
                    <Link
                      key={tool.id}
                      to={tool.basePath}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${
                        currentPath.startsWith(tool.basePath)
                          ? "bg-primary/10 text-primary"
                          : "text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <ToolIcon icon={tool.icon} size={22} />
                      <span className="font-medium">{tool.name()}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Current tool's sub-pages */}
            {currentTool && hasSub && (
              <div>
                <div className="px-1 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  {currentTool.name()}
                </div>
                <div className="space-y-1">
                  {pages.map((page) => (
                    <a
                      key={page.path}
                      href={page.path}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${
                        page.path === currentPath
                          ? "bg-primary/10 text-primary"
                          : "text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <ToolIcon icon={page.icon} size={20} />
                      <span className="font-medium">{page.name()}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Auth */}
            <div className="border-t border-border px-1 pt-3">
              <UserMenu />
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
